import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { customerApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
    Building, 
    Globe, 
    Target, 
    Users,
    Lightbulb,
    Shield,
    Save,
    CheckCircle,
    Clock
} from 'lucide-react';

export default function CustomerDetailsPage() {
    const { user, updateUser, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [details, setDetails] = useState({
        company_name: '',
        website: '',
        company_summary: '',
        business_objectives: '',
        solutions: [],
        value_propositions: [],
        priority_offerings: { primary: [], secondary: [] },
        competitive_differentiation: [],
        usps: { technical: [], business: [], delivery: [] },
        pain_points: [],
        emotional_triggers: [],
        objection_handling: [],
        why_act_now: [],
        target_industries: [],
        target_company_sizes: [],
        target_geography: [],
        target_personas: { decision_makers: [], influencers: [], champions: [] },
        icp_summary: '',
        account_tiering_logic: '',
        buying_committee_mapping: '',
    });

    useEffect(() => {
        loadDetails();
    }, []);

    const loadDetails = async () => {
        try {
            const response = await customerApi.getDetails();
            if (response.data) {
                setDetails(response.data);
            } else {
                // Pre-fill company name
                setDetails(prev => ({
                    ...prev,
                    company_name: user?.company_name || '',
                }));
            }
        } catch (error) {
            console.error('Failed to load details');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await customerApi.saveDetails(details);
            updateUser({ ...user, is_onboarded: true });
            toast.success('Details saved successfully');
        } catch (error) {
            toast.error('Failed to save details');
        } finally {
            setSaving(false);
        }
    };

    const handleArrayInput = (field, value) => {
        const items = value.split('\n').filter(item => item.trim());
        setDetails(prev => ({ ...prev, [field]: items }));
    };

    const handleNestedArrayInput = (parent, child, value) => {
        const items = value.split('\n').filter(item => item.trim());
        setDetails(prev => ({
            ...prev,
            [parent]: { ...prev[parent], [child]: items }
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="customer-details-page">
            <Header 
                title="Company Details"
                subtitle="Your ABM account information and targeting criteria"
            />

            <div className="p-8">
                {/* Status Banner */}
                <div className={`mb-6 p-4 rounded-lg border ${
                    details.is_approved 
                        ? 'bg-status-success/10 border-status-success/30' 
                        : 'bg-status-warning/10 border-status-warning/30'
                }`}>
                    <div className="flex items-center gap-3">
                        {details.is_approved ? (
                            <>
                                <CheckCircle className="w-5 h-5 text-status-success" />
                                <span className="text-status-success font-medium">Account Approved</span>
                            </>
                        ) : (
                            <>
                                <Clock className="w-5 h-5 text-status-warning" />
                                <span className="text-status-warning font-medium">Pending Approval</span>
                                <span className="text-content-secondary text-sm">- Complete all sections to expedite review</span>
                            </>
                        )}
                    </div>
                </div>

                <Tabs defaultValue="company" className="space-y-6">
                    <TabsList className="bg-surface-card border border-line-default p-1">
                        <TabsTrigger value="company" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                            <Building className="w-4 h-4 mr-2" />
                            Company
                        </TabsTrigger>
                        <TabsTrigger value="solutions" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                            <Lightbulb className="w-4 h-4 mr-2" />
                            Solutions
                        </TabsTrigger>
                        <TabsTrigger value="targeting" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                            <Target className="w-4 h-4 mr-2" />
                            Targeting
                        </TabsTrigger>
                        <TabsTrigger value="personas" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                            <Users className="w-4 h-4 mr-2" />
                            Personas
                        </TabsTrigger>
                    </TabsList>

                    {/* Company Tab */}
                    <TabsContent value="company" className="space-y-6">
                        <div className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">
                                COMPANY OVERVIEW
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Company Name</Label>
                                    <Input
                                        value={details.company_name}
                                        onChange={(e) => setDetails({ ...details, company_name: e.target.value })}
                                        className="input-base"
                                        placeholder="Your Company"
                                        data-testid="company-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Website</Label>
                                    <Input
                                        value={details.website}
                                        onChange={(e) => setDetails({ ...details, website: e.target.value })}
                                        className="input-base"
                                        placeholder="https://yourcompany.com"
                                        data-testid="website-input"
                                    />
                                </div>
                            </div>
                            <div className="mt-6 space-y-2">
                                <Label className="text-content-secondary">Company Summary</Label>
                                <Textarea
                                    value={details.company_summary}
                                    onChange={(e) => setDetails({ ...details, company_summary: e.target.value })}
                                    className="input-base min-h-[100px]"
                                    placeholder="Brief description of your company..."
                                    data-testid="company-summary-input"
                                />
                            </div>
                            <div className="mt-6 space-y-2">
                                <Label className="text-content-secondary">Business Objectives for ABM</Label>
                                <Textarea
                                    value={details.business_objectives}
                                    onChange={(e) => setDetails({ ...details, business_objectives: e.target.value })}
                                    className="input-base min-h-[100px]"
                                    placeholder="What do you want to achieve with ABM?"
                                    data-testid="objectives-input"
                                />
                            </div>
                        </div>
                    </TabsContent>

                    {/* Solutions Tab */}
                    <TabsContent value="solutions" className="space-y-6">
                        <div className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">
                                SOLUTIONS & VALUE PROPOSITIONS
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Solutions Being Positioned (one per line)</Label>
                                    <Textarea
                                        value={details.solutions?.join('\n')}
                                        onChange={(e) => handleArrayInput('solutions', e.target.value)}
                                        className="input-base min-h-[120px]"
                                        placeholder="Solution 1&#10;Solution 2&#10;Solution 3"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Value Propositions (one per line)</Label>
                                    <Textarea
                                        value={details.value_propositions?.join('\n')}
                                        onChange={(e) => handleArrayInput('value_propositions', e.target.value)}
                                        className="input-base min-h-[120px]"
                                        placeholder="Value prop 1&#10;Value prop 2"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Competitive Differentiation (one per line)</Label>
                                    <Textarea
                                        value={details.competitive_differentiation?.join('\n')}
                                        onChange={(e) => handleArrayInput('competitive_differentiation', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="What makes you different?"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Pain Points Solved (one per line)</Label>
                                    <Textarea
                                        value={details.pain_points?.join('\n')}
                                        onChange={(e) => handleArrayInput('pain_points', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="Customer pain points"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">
                                USPs & MESSAGING
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Technical Advantages</Label>
                                    <Textarea
                                        value={details.usps?.technical?.join('\n')}
                                        onChange={(e) => handleNestedArrayInput('usps', 'technical', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="Technical USPs"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Business Advantages</Label>
                                    <Textarea
                                        value={details.usps?.business?.join('\n')}
                                        onChange={(e) => handleNestedArrayInput('usps', 'business', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="Business USPs"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Delivery Advantages</Label>
                                    <Textarea
                                        value={details.usps?.delivery?.join('\n')}
                                        onChange={(e) => handleNestedArrayInput('usps', 'delivery', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="Delivery/Experience USPs"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Objection Handling Points</Label>
                                    <Textarea
                                        value={details.objection_handling?.join('\n')}
                                        onChange={(e) => handleArrayInput('objection_handling', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="Common objections and responses"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">"Why Act Now" Drivers</Label>
                                    <Textarea
                                        value={details.why_act_now?.join('\n')}
                                        onChange={(e) => handleArrayInput('why_act_now', e.target.value)}
                                        className="input-base min-h-[100px]"
                                        placeholder="Urgency drivers"
                                    />
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Targeting Tab */}
                    <TabsContent value="targeting" className="space-y-6">
                        <div className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">
                                TARGET CRITERIA
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Target Industries (one per line)</Label>
                                    <Textarea
                                        value={details.target_industries?.join('\n')}
                                        onChange={(e) => handleArrayInput('target_industries', e.target.value)}
                                        className="input-base min-h-[120px]"
                                        placeholder="Financial Services&#10;Healthcare&#10;Manufacturing"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Target Company Sizes</Label>
                                    <Textarea
                                        value={details.target_company_sizes?.join('\n')}
                                        onChange={(e) => handleArrayInput('target_company_sizes', e.target.value)}
                                        className="input-base min-h-[120px]"
                                        placeholder="1000-5000 employees&#10;5000+ employees"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Target Geography</Label>
                                    <Textarea
                                        value={details.target_geography?.join('\n')}
                                        onChange={(e) => handleArrayInput('target_geography', e.target.value)}
                                        className="input-base min-h-[120px]"
                                        placeholder="United States&#10;Canada&#10;United Kingdom"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">
                                ICP & ACCOUNT STRATEGY
                            </h3>
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">ICP Definition Summary</Label>
                                    <Textarea
                                        value={details.icp_summary}
                                        onChange={(e) => setDetails({ ...details, icp_summary: e.target.value })}
                                        className="input-base min-h-[100px]"
                                        placeholder="Describe your ideal customer profile..."
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label className="text-content-secondary">Account Tiering Logic</Label>
                                        <Textarea
                                            value={details.account_tiering_logic}
                                            onChange={(e) => setDetails({ ...details, account_tiering_logic: e.target.value })}
                                            className="input-base min-h-[100px]"
                                            placeholder="How do you tier accounts?"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-content-secondary">Buying Committee Mapping</Label>
                                        <Textarea
                                            value={details.buying_committee_mapping}
                                            onChange={(e) => setDetails({ ...details, buying_committee_mapping: e.target.value })}
                                            className="input-base min-h-[100px]"
                                            placeholder="Typical buying committee structure"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Personas Tab */}
                    <TabsContent value="personas" className="space-y-6">
                        <div className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">
                                TARGET PERSONAS
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary flex items-center gap-2">
                                        <Badge className="badge badge-error">Decision Makers</Badge>
                                    </Label>
                                    <Textarea
                                        value={details.target_personas?.decision_makers?.join('\n')}
                                        onChange={(e) => handleNestedArrayInput('target_personas', 'decision_makers', e.target.value)}
                                        className="input-base min-h-[150px]"
                                        placeholder="CTO&#10;CIO&#10;VP of Engineering"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary flex items-center gap-2">
                                        <Badge className="badge badge-warning">Influencers</Badge>
                                    </Label>
                                    <Textarea
                                        value={details.target_personas?.influencers?.join('\n')}
                                        onChange={(e) => handleNestedArrayInput('target_personas', 'influencers', e.target.value)}
                                        className="input-base min-h-[150px]"
                                        placeholder="Director of IT&#10;Enterprise Architect"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary flex items-center gap-2">
                                        <Badge className="badge badge-success">Champions</Badge>
                                    </Label>
                                    <Textarea
                                        value={details.target_personas?.champions?.join('\n')}
                                        onChange={(e) => handleNestedArrayInput('target_personas', 'champions', e.target.value)}
                                        className="input-base min-h-[150px]"
                                        placeholder="Senior Developer&#10;IT Manager"
                                    />
                                </div>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Save Button */}
                <div className="mt-8 flex justify-end">
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn-primary gap-2"
                        data-testid="save-details-btn"
                    >
                        {saving ? (
                            <>
                                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                SAVE DETAILS
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
